import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CalendarDays,
  BookOpen,
  Images,
  SlidersHorizontal,
  Settings,
  Plus,
  Menu,
  X,
  Sun,
  Moon,
  CheckCheck,
  Quote,
} from 'lucide-react';
import { Toaster } from './ui/toaster';
import '../styles/editorial-studio.css';

const navigation = [
  { label: 'Review desk', href: '/', icon: CheckCheck },
  { label: 'Publishing calendar', href: '/calendar', icon: CalendarDays },
  { label: 'Article library', href: '/articles', icon: BookOpen },
  { label: 'Image library', href: '/images', icon: Images },
  { label: 'Automation', href: '/automation', icon: SlidersHorizontal },
  { label: 'Quote guides', href: '/quote-studio', icon: Quote },
];
export default function StudioLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(() =>
    localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'
  );
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.body.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);
  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);
  const active = (href: string) =>
    href === '/'
      ? pathname === '/' ||
        pathname === '/editorial' ||
        pathname.startsWith('/review/')
      : pathname.startsWith(href);
  return (
    <div className="studio-shell">
      <a className="studio-skip" href="#studio-main">
        Skip to content
      </a>
      {open && (
        <button
          className="studio-scrim"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <aside className={`studio-sidebar ${open ? 'is-open' : ''}`}>
        <Link to="/" className="studio-brand">
          Article Creator
        </Link>
        <button
          className="studio-mobile-close"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        >
          <X size={20} />
        </button>
        <nav aria-label="Main navigation">
          {navigation.map(({ label, href, icon: Icon }) => (
            <Link
              key={href}
              to={href}
              aria-current={active(href) ? 'page' : undefined}
              className={active(href) ? 'is-active' : ''}
            >
              <Icon size={18} />
              <span>{label}</span>
            </Link>
          ))}
        </nav>
        <Link className="studio-new" to="/create">
          <Plus size={18} /> Create article
        </Link>
        <div className="studio-sidebar-bottom">
          <div className="studio-sidebar-controls">
            <Link to="/settings" className="studio-settings">
              <Settings size={17} /> Settings
            </Link>
            <button
              className="studio-icon"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>
          <details className="studio-tools">
            <summary>Other tools</summary>
            <Link to="/quotes">Quotes</Link>
            <Link to="/specialized">Specialized workspace</Link>
          </details>
        </div>
      </aside>
      <div className="studio-body">
        <main id="studio-main" className="studio-main" tabIndex={-1}>
          <button
            className="studio-mobile-toggle"
            aria-label="Open navigation"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <Menu size={20} /> Navigation
          </button>
          {children}
        </main>
      </div>
      <Toaster />
    </div>
  );
}
